import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Products.css';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons';
import ProductosLoading from '../ProductosLoading/ProductosLoading';
import { Link as Anchor } from "react-router-dom";
import { useCatalogData } from '../CatalogData/CatalogDataProvider';
import moneda from '../moneda';

SwiperCore.use([Navigation, Pagination, Autoplay]);

export default function Products() {
    const {
        productos,
        categorias,
        subcategorias,
        status,
        ensureProductos,
        ensureCategorias,
        ensureSubcategorias,
        retryProductos,
        retryCategorias,
        retrySubcategorias,
    } = useCatalogData();
    const [fixedCategories, setFixedCategories] = useState(false);
    const categoriasInputRef = useRef(null);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('Todo');
    const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState(null);
    const loading = status.productos === 'idle' || status.productos === 'loading';
    const productError = status.productos === 'error';
    const auxiliaryError = status.categorias === 'error' || status.subcategorias === 'error';

    const handleClickCategoria = (categoria) => {
        setCategoriaSeleccionada(categoria === 'Todo' ? 'Todo' : String(categoria));
        setSubcategoriaSeleccionada('Todo');
    };

    const handleClickSubcategoria = (subcategoria) => {
        setSubcategoriaSeleccionada(subcategoria === 'Todo' ? 'Todo' : String(subcategoria));
    };

    const handleScroll = useCallback(() => {
        if (categoriasInputRef.current) {
            const bannerElement = document.querySelector('.BannerContain');
            const bannerHeight = bannerElement ? bannerElement.offsetHeight : 0;

            if (window.scrollY > categoriasInputRef.current.offsetTop && window.scrollY > bannerHeight) {
                setFixedCategories(true);
            } else {
                setFixedCategories(false);
            }
        }
    }, []);

    useEffect(() => {
        ensureProductos().catch(() => {});
        ensureCategorias().catch(() => {});
        ensureSubcategorias().catch(() => {});
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [ensureCategorias, ensureProductos, ensureSubcategorias, handleScroll]);
    const handleRetryProducts = useCallback(() => {
        retryProductos().catch(() => {});
    }, [retryProductos]);

    const handleRetryAuxiliary = useCallback(() => {
        if (status.categorias === 'error') {
            retryCategorias().catch(() => {});
        }
        if (status.subcategorias === 'error') {
            retrySubcategorias().catch(() => {});
        }
    }, [
        retryCategorias,
        retrySubcategorias,
        status.categorias,
        status.subcategorias,
    ]);

    const obtenerImagen = (item) => {
        if (item.imagen1) {
            return item.imagen1;
        } else if (item.imagen2) {
            return item.imagen2;
        } else if (item.imagen3) {
            return item.imagen3;
        } else if (item.imagen4) {
            return item.imagen4;
        }
        return null;
    };

    const categoriasOrdenadas = useMemo(
        () => [...categorias].sort((firstCategory, secondCategory) => (
            Number(firstCategory?.orden ?? 0) - Number(secondCategory?.orden ?? 0)
        )),
        [categorias]
    );

    const categoriasConProductos = useMemo(
        () => categoriasOrdenadas.filter(categoria =>
            productos.some(producto => (
                String(producto?.idCategoria ?? '') === String(categoria?.idCategoria ?? '')
            ))
        ),
        [categoriasOrdenadas, productos]
    );

    const subcategoriasVisibles = useMemo(
        () => subcategorias.filter(subcategoria => (
            String(subcategoria?.idCategoria ?? '') === categoriaSeleccionada
            && productos.some(producto => (
                String(producto?.idSubCategoria ?? '') === String(subcategoria?.idSubCategoria ?? '')
            ))
        )),
        [categoriaSeleccionada, productos, subcategorias]
    );

    return (
        <div className='ProductsContain'>
            {!loading && !productError && auxiliaryError && (
                <div role='status'>
                    <span>No pudimos cargar algunos filtros.</span>
                    <button type='button' onClick={handleRetryAuxiliary}>
                        Reintentar filtros
                    </button>
                </div>
            )}
            {!loading && !productError && productos.length > 0 && (
                <div className={`categoriasInputs ${fixedCategories ? 'fixed' : ''}`} id='FlexIn' ref={categoriasInputRef} aria-label="Filtros de productos">
                    <div className='categoriasInputsFilter' >
                        <input
                            type="button"
                            value="Todo"
                            onClick={() => handleClickCategoria('Todo')}
                            aria-pressed={categoriaSeleccionada === 'Todo'}
                            aria-label="Ver todos los productos"
                            style={{
                                backgroundColor: categoriaSeleccionada === 'Todo' ? '#023992' : '',
                                color: categoriaSeleccionada === 'Todo' ? '#fff' : '',
                            }}
                        />
                        {categoriasConProductos?.map(({ categoria, idCategoria }) => (
                            <div className='columnInput' key={idCategoria}>
                                <input
                                    type="button"
                                    value={categoria}
                                    onClick={() => handleClickCategoria(idCategoria)}
                                    aria-pressed={categoriaSeleccionada === String(idCategoria)}
                                    aria-label={`Filtrar productos por ${categoria}`}
                                    style={{
                                        backgroundColor: categoriaSeleccionada === String(idCategoria) ? '#023992' : '',
                                        color: categoriaSeleccionada === String(idCategoria) ? '#fff' : '',
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    {categoriaSeleccionada !== 'Todo' &&
                        subcategoriasVisibles.length > 0 && (
                            <div className='categoriasInputsFilter' id='subcategoriasInputs' aria-label="Filtros de subcategorías">
                                <input
                                    type="button"
                                    value="Todo"
                                    onClick={() => handleClickSubcategoria('Todo')}
                                    aria-pressed={subcategoriaSeleccionada === 'Todo'}
                                    aria-label="Ver todas las subcategorías"
                                    style={{
                                        borderBottom: subcategoriaSeleccionada === 'Todo' ? '2px solid #023992' : '',
                                        color: subcategoriaSeleccionada === 'Todo' ? '#023992' : '',
                                    }}
                                    id='subcategoriaInput'
                                />

                                {subcategoriasVisibles.map(subcategoria => (
                                        <div key={subcategoria.idSubCategoria}>
                                            <input
                                                type="button"
                                                value={subcategoria.subcategoria}
                                                onClick={() => handleClickSubcategoria(subcategoria.idSubCategoria)}
                                                aria-pressed={subcategoriaSeleccionada === String(subcategoria.idSubCategoria)}
                                                aria-label={`Filtrar productos por ${subcategoria.subcategoria}`}
                                                style={{
                                                    borderBottom: subcategoriaSeleccionada === String(subcategoria.idSubCategoria) ? '2px solid #023992' : '',
                                                    color: subcategoriaSeleccionada === String(subcategoria.idSubCategoria) ? '#023992' : '',
                                                }}
                                                id='subcategoriaInput'

                                            />
                                        </div>
                                    ))}
                            </div>
                        )}



                </div>
            )}


            {loading ? (
                <ProductosLoading />
            ) : productError ? (
                <div role='alert'>
                    <p>No pudimos cargar los productos.</p>
                    <button type='button' onClick={handleRetryProducts}>
                        Reintentar productos
                    </button>
                </div>
            ) : productos.length === 0 ? (
                <div role='status'>
                    <p>No hay productos disponibles.</p>
                </div>
            ) : (
                <div className='Products'>
                    {categoriaSeleccionada === 'Todo' && (
                        <>
                            {productos?.some(item => item.masVendido === "si") && (
                                <div className='categoriSection'>
                                    <Swiper
                                        effect={'coverflow'}
                                        grabCursor={true}
                                        slidesPerView={'auto'}
                                        id='swiper_container_products'
                                        autoplay={{ delay: 3000 }}
                                        aria-label="Productos más vendidos"
                                    >
                                        {productos?.filter(item => item.masVendido === "si")?.slice(0, 20)?.map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products-masvendidos'>
                                                <Anchor className='cardProdcutmasVendido' to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`} aria-label={`Ver producto ${item.titulo}`}>
                                                    <img
                                                        src={obtenerImagen(item)}
                                                        alt={item.titulo}
                                                        loading='lazy'
                                                        decoding='async'
                                                    />
                                                    <h6 className='masVendido'>Más Vendido</h6>
                                                    <div className='cardText'>
                                                        <h4>{item.titulo}</h4>
                                                        <span>{item.descripcion}</span>
                                                        <div className='deFLexPrice'>
                                                            <h5> {moneda} {String(item?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                            {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                                                                <h5 className='precioTachado'>{moneda} {`${item?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Anchor>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {categoriasConProductos?.map(({ categoria, idCategoria }) => (
                                <div key={idCategoria} className='categoriSection'>
                                    <div className='deFlexTitlesection'>
                                        <h3>{categoria}</h3>
                                        <button type="button" onClick={() => handleClickCategoria(idCategoria)} aria-label={`Ver más productos de ${categoria}`}>
                                            Ver más
                                        </button>
                                    </div>

                                    <Swiper
                                        effect={'coverflow'}
                                        grabCursor={true}
                                        slidesPerView={'auto'}
                                        id='swiper_container_products'
                                        aria-label={`Productos de ${categoria}`}
                                    >


                                        {productos
                                            .filter(item => (
                                                String(item?.idCategoria ?? '') === String(idCategoria ?? '')
                                            ))
                                            .map(item => (
                                            <SwiperSlide id='SwiperSlide-scroll-products' key={item.idProducto}>
                                                <Anchor className='cardProdcut' key={item.idProducto} to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`} aria-label={`Ver producto ${item.titulo}`}>

                                                    <img
                                                        src={obtenerImagen(item)}
                                                        alt={item.titulo}
                                                        loading='lazy'
                                                        decoding='async'
                                                    />
                                                    <div className='cardText'>
                                                        <h4>{item.titulo}</h4>
                                                        <span>{item.descripcion}</span>
                                                        <div className='deFLexPrice'>
                                                            <h5> {moneda} {String(item?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                            {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                                                                <h5 className='precioTachado'>{moneda} {`${item?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                            )}
                                                        </div>
                                                        <FontAwesomeIcon icon={faAngleDoubleRight} className='iconCard' />
                                                    </div>

                                                </Anchor>
                                            </SwiperSlide>
                                            ))}

                                    </Swiper>
                                </div>

                            ))}
                        </>
                    )}

                    <div className='categoriSectionSelected'>
                        {productos
                            ?.filter(item => {
                                if (categoriaSeleccionada === 'Todo') {
                                    return categoriasConProductos.length === 0;
                                }
                                // Si "Todo" está seleccionado en subcategoría, muestra todos los productos de la categoría
                                if (subcategoriaSeleccionada === 'Todo' || !subcategoriaSeleccionada) {
                                    return String(item?.idCategoria ?? '') === categoriaSeleccionada;
                                }
                                // Si hay subcategoría seleccionada, filtra por subcategoría
                                return String(item?.idSubCategoria ?? '') === subcategoriaSeleccionada;
                            })
                            ?.map(item => (
                                <Anchor key={item.idProducto} to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`} aria-label={`Ver producto ${item.titulo}`}>
                                    <div className='cardProdcutSelected'>
                                        <img
                                            src={obtenerImagen(item)}
                                            alt={item.titulo}
                                            loading='lazy'
                                            decoding='async'
                                        />
                                        <div className='cardTextSelected'>
                                            <h4>{item.titulo}</h4>
                                            <span>{item.descripcion}</span>
                                            <div className='deFLexPrice'>
                                                <h5> {moneda} {String(item?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                {(item.precioAnterior >= 1 && item.precioAnterior !== undefined) && (
                                                    <h5 className='precioTachado'>{moneda} {`${item?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                )}
                                            </div>
                                            <FontAwesomeIcon icon={faAngleDoubleRight} className='iconCard' />
                                        </div>
                                    </div>
                                </Anchor>
                            ))}
                    </div>


                </div>
            )}
        </div>
    );
}
